import React, {FC} from 'react'
import classNames from 'classnames'
import {DopeProduct} from 'interfaces'

const detectionKeys = [
  'PHI',
  'PII',
  'IP',
  'PCI',
  'OTHER',
  'UNCLASSIFIED',
  'PAYWALLED',
] as const

export type DetectionType = (typeof detectionKeys)[number]

export const paywalledChip = ['PAYWALLED']

export interface DetectionChipProps {
  type: DetectionType
  children: React.ReactNode
  onChipClick: (chip: string) => void
  className?: string
  dataName?: string
  dataCy?: string
}

export interface DetectionChipsProps {
  title: string
  chips: string[] | undefined
  product: DopeProduct
  onChipClick: (chip: string) => void
  chipClasses?: string
  isPrivate?: boolean
  className?: string
}

// Utility function to convert string to DetectionKey
const isDetectionKey = (v: string): v is DetectionType =>
  (detectionKeys as readonly string[]).includes(v as DetectionType)

export const toDetectionKey = (value?: string): DetectionType => {
  const k = (value ?? '').toUpperCase()
  return isDetectionKey(k) ? k : 'UNCLASSIFIED'
}

// Tailwind CSS theme mapping for detection chips
const getDetectionChipClasses = (type: DetectionType, isPrivate?: boolean) => {
  const baseClasses =
    'inline-flex items-center px-2 py-0 text-xs font-semibold rounded-full border karla leading-4.5'

  const themeClasses = {
    PHI: 'bg-dope-purple-50/20 border-dope-purple-50',
    PII: 'bg-dope-purple-500/30 border-dope-purple-500',
    IP: 'bg-yellow-100/15 border-yellow-100',
    PCI: 'bg-pink-500/30 border-pink-500',
    OTHER: 'bg-purple-1500/30 border-purple-1500/75',
    UNCLASSIFIED: 'bg-cyan-600/30 border-cyan-600 cursor-auto',
    PAYWALLED: 'bg-white/20 border-gray-1000 cursor-auto',
  }

  const privateOverlay = isPrivate ? 'bg-white/60' : ''

  return classNames(baseClasses, themeClasses[type], privateOverlay)
}

const DetectionChip: React.FC<DetectionChipProps> = ({
  type,
  children,
  className,
  dataName,
  dataCy,
  onChipClick,
}) => {
  const handleClick = (): void => {
    if (type !== 'UNCLASSIFIED') {
      onChipClick(type.toLowerCase())
    }
  }

  return (
    <button
      className={classNames(getDetectionChipClasses(type), className)}
      data-name={dataName}
      data-cy={dataCy}
      onClick={handleClick}
      disabled={type === 'PAYWALLED'}
    >
      {children}
    </button>
  )
}

export const DetectionChips: FC<DetectionChipsProps> = ({
  title,
  chips,
  chipClasses,
  isPrivate = false,
  className,
  product,
  onChipClick,
}) => {
  if (!chips || chips.length === 0) {
    return null
  }

  return (
    <div
      className="flex items-center flex-wrap gap-2"
      data-cy={`${product}-detection-chips`}
    >
      <div className="karla text-sm leading-3.5">{title}</div>
      <div className={classNames('flex gap-1', className)}>
        {chips.map(chip => {
          const uniqueKey = `${product}-detection-chip-${chip?.toLowerCase()}`
          return (
            <DetectionChip
              key={uniqueKey}
              className={classNames('uppercase', chipClasses, {
                capitalize: chip.toLowerCase() === 'other',
                'opacity-50': isPrivate,
              })}
              onChipClick={onChipClick}
              type={toDetectionKey(chip)}
              dataName={uniqueKey}
              dataCy={uniqueKey}
            >
              {chip === 'PAYWALLED' ? '???' : chip}
            </DetectionChip>
          )
        })}
      </div>
    </div>
  )
}
